import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NotificationsService } from '@app/core';
import { SharedModule } from '@app/shared';
import {
  TuiButton,
  TuiDataList,
  TuiDialogContext,
  TuiError,
  TuiHint,
  TuiIcon,
  TuiLabel,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  TuiButtonLoading,
  TuiChevron,
  TuiFieldErrorPipe,
  TuiInputChip,
  TuiInputNumber,
  TuiMultiSelect,
  TuiTextarea,
  TuiTooltip,
} from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { ContactFormService } from '../../../contact/services';

interface Feature {
  readonly id: string;
  readonly label: string;
}

@Component({
  selector: 'app-pricing-request-custom-plan-dialog',
  imports: [
    SharedModule,
    TuiButton,
    TuiButtonLoading,
    TuiChevron,
    TuiDataList,
    TuiError,
    TuiFieldErrorPipe,
    TuiHint,
    TuiIcon,
    TuiInputChip,
    TuiInputNumber,
    TuiLabel,
    TuiMultiSelect,
    TuiTextarea,
    TuiTextfield,
    TuiTooltip,
  ],
  templateUrl: './pricing-request-custom-plan-dialog.component.html',
  styleUrl: './pricing-request-custom-plan-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingRequestCustomPlanDialogComponent {
  protected readonly context =
    inject<TuiDialogContext<void>>(POLYMORPHEUS_CONTEXT);
  protected readonly notificationsService = inject(NotificationsService);
  protected readonly contactFormService = inject(ContactFormService);

  protected readonly formGroup = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    company: new FormControl(''),
    email: new FormControl('', [Validators.required, Validators.email]),
    features: new FormControl<Feature[]>([], [Validators.required]),
    monthlyActiveDevices: new FormControl<number | null>(null),
    updatesPerMonth: new FormControl<number | null>(null),
    updateSizeInMb: new FormControl<number | null>(null),
    buildsPerMonth: new FormControl<number | null>(null),
    submissionsPerMonth: new FormControl<number | null>(null),
    additionalComments: new FormControl(''),
  });

  protected readonly submitContactForm = this.contactFormService.submit();

  protected readonly features: readonly Feature[] = [
    { id: 'live-updates', label: 'Live Updates' },
    { id: 'native-builds', label: 'Native Builds' },
    { id: 'app-store-publishing', label: 'App Store Publishing' },
  ];

  constructor() {
    // Watch for changes in selected features and update validators
    this.formGroup.controls.features.valueChanges.subscribe(features => {
      this.updateConditionalValidators(features || []);
    });
  }

  protected readonly stringifyFeature = (feature: Feature): string =>
    feature.label;

  protected get selectedFeatures(): Feature[] {
    return this.formGroup.value.features || [];
  }

  protected get showLiveUpdatesFields(): boolean {
    return this.selectedFeatures.some(feature => feature.id === 'live-updates');
  }

  protected get showNativeBuildsFields(): boolean {
    return this.selectedFeatures.some(
      feature => feature.id === 'native-builds',
    );
  }

  protected get showAppStorePublishingFields(): boolean {
    return this.selectedFeatures.some(
      feature => feature.id === 'app-store-publishing',
    );
  }

  private updateConditionalValidators(features: Feature[]): void {
    const hasLiveUpdates = features.some(f => f.id === 'live-updates');
    const hasNativeBuilds = features.some(f => f.id === 'native-builds');
    const hasAppStorePublishing = features.some(
      f => f.id === 'app-store-publishing',
    );

    // Update Live Updates field validators
    if (hasLiveUpdates) {
      this.formGroup.controls.monthlyActiveDevices.setValidators([
        Validators.required,
      ]);
      this.formGroup.controls.updatesPerMonth.setValidators([
        Validators.required,
      ]);
      this.formGroup.controls.updateSizeInMb.setValidators([
        Validators.required,
      ]);
    } else {
      this.formGroup.controls.monthlyActiveDevices.clearValidators();
      this.formGroup.controls.updatesPerMonth.clearValidators();
      this.formGroup.controls.updateSizeInMb.clearValidators();
    }

    // Update Native Builds field validators
    if (hasNativeBuilds) {
      this.formGroup.controls.buildsPerMonth.setValidators([
        Validators.required,
      ]);
    } else {
      this.formGroup.controls.buildsPerMonth.clearValidators();
    }

    // Update App Store Publishing field validators
    if (hasAppStorePublishing) {
      this.formGroup.controls.submissionsPerMonth.setValidators([
        Validators.required,
      ]);
    } else {
      this.formGroup.controls.submissionsPerMonth.clearValidators();
    }

    // Update validity for all conditional fields
    this.formGroup.controls.monthlyActiveDevices.updateValueAndValidity();
    this.formGroup.controls.updatesPerMonth.updateValueAndValidity();
    this.formGroup.controls.updateSizeInMb.updateValueAndValidity();
    this.formGroup.controls.buildsPerMonth.updateValueAndValidity();
    this.formGroup.controls.submissionsPerMonth.updateValueAndValidity();
  }

  protected async handleSubmit(): Promise<void> {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      return;
    }

    const formValue = this.formGroup.value;
    if (
      !formValue.firstName ||
      !formValue.lastName ||
      !formValue.email ||
      !formValue.features ||
      formValue.features.length === 0
    ) {
      return;
    }

    // Format the message with all the details
    const featuresList = formValue.features
      .map((f: Feature) => f.label)
      .join(', ');
    let message = `Custom Pricing Plan Request\n\n`;
    message += `Features of Interest: ${featuresList}\n\n`;

    if (this.showLiveUpdatesFields) {
      message += `Live Updates Details:\n`;
      message += `- Monthly Active Devices: ${formValue.monthlyActiveDevices}\n`;
      message += `- Updates per Month: ${formValue.updatesPerMonth}\n`;
      message += `- Update Size: ${formValue.updateSizeInMb} MB\n\n`;
    }

    if (this.showNativeBuildsFields) {
      message += `Native Builds Details:\n`;
      message += `- Builds per Month: ${formValue.buildsPerMonth}\n\n`;
    }

    if (this.showAppStorePublishingFields) {
      message += `App Store Publishing Details:\n`;
      message += `- Submissions per Month: ${formValue.submissionsPerMonth}\n\n`;
    }

    if (formValue.additionalComments) {
      message += `Additional Comments:\n${formValue.additionalComments}`;
    }

    try {
      await this.submitContactForm.mutateAsync({
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        company: formValue.company || undefined,
        email: formValue.email,
        message: message.trim(),
      });

      void this.notificationsService.showSuccessNotification(
        'Your custom plan request has been sent successfully! We will get back to you soon.',
      );
      this.context.completeWith();
    } catch {
      void this.notificationsService.showErrorNotification(
        'Failed to submit your request. Please try again.',
      );
    }
  }
}
